import React from "react";
import styled, { css, DefaultTheme } from "styled-components";
import { Check, Star } from "lucide-react";
import {
  LanguageNameSpaces,
  MarketplaceSubGroup,
  TagName,
} from "@shared/types";
import { TAG_NAME_TO_PROPERTY_NAME } from "@shared/common/constants";
import { SUB_GROUP_CARD_TAGS } from "@shared/features/newMarketplace/constants";
import { MIN_NUM_OF_REVIEWS } from "@shared/features/newMarketplace/constants";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import {
  directionColor,
  getNewLookTokens,
  NEW_LOOK_RADIUS,
  newLookPhoneType,
  newLookType,
} from "common/NewLook/tokens.ts";
import { hexToRGB } from "@shared/utils/misc";
import { formatSignedPercent } from "features/newMarketplace/NewLook/format.ts";
import { phoneMaxWidth } from "common/NewLook/layout.ts";

const LOGO_PALETTE = [
  "#3566D8",
  "#7B4FE0",
  "#16A394",
  "#D9822B",
  "#DB5A8E",
  "#2F8F5B",
];

/** A stable colour per channel name, so the same channel always looks alike. */
const logoColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = (hash * 31 + name.charCodeAt(i)) >>> 0;
  return LOGO_PALETTE[hash % LOGO_PALETTE.length];
};

/** Channel logo: the image, or a white letter on a coloured tile. */
export const GroupLogo = ({
  image,
  name,
  size = 40,
}: {
  image?: string;
  name?: string;
  size?: number;
}) =>
  image ? (
    <Logo src={image} alt="" $size={size} />
  ) : (
    <Logo
      as="span"
      aria-hidden="true"
      $size={size}
      $color={logoColor(name?.trim())}
    >
      {name?.trim().slice(0, 1).toUpperCase()}
    </Logo>
  );

export const GroupCompactTitle = ({
  image,
  name,
}: {
  image?: string;
  name?: string;
}) => (
  <CompactTitle>
    <GroupLogo image={image} name={name} size={24} />
    <CompactName>{name}</CompactName>
  </CompactTitle>
);

const STAR_COUNT = 5;

/** Five small stars filled up to the rating, "4.8" and "(120)". */
export const GroupRatingInline = ({
  avgStars,
  numReviewers,
}: {
  avgStars?: number | null;
  numReviewers?: number;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  if ((numReviewers ?? 0) < MIN_NUM_OF_REVIEWS || !avgStars || avgStars < 0) {
    return <Muted>{t("rating.not_enough_reviews")}</Muted>;
  }
  return (
    <Rating>
      <Stars aria-hidden="true">
        {Array.from({ length: STAR_COUNT }, (_, index) => (
          <Star
            key={index}
            size={13}
            strokeWidth={0}
            fill="currentColor"
            className={avgStars >= index + 0.75 ? undefined : "off"}
          />
        ))}
      </Stars>
      <strong>{avgStars.toFixed(1)}</strong>
      <Muted>({numReviewers})</Muted>
    </Rating>
  );
};

type FigureRole = "figure" | "figureLarge" | "meta";

/** A signed percentage in its direction colour, in a typography role. */
export const PnlValue = ({
  value,
  role = "figure",
  className,
}: {
  value: number;
  role?: FigureRole;
  className?: string;
}) => (
  <Pnl $value={value} $role={role} className={className}>
    {formatSignedPercent(value)}
  </Pnl>
);

/** Price level as "$$" with the unused dollars on a hairline tone. */
export const PriceDollars = ({ priceNumber }: { priceNumber: number }) => (
  <Dollars aria-label={`${priceNumber}/4`}>
    {"$".repeat(Math.max(0, Math.min(priceNumber, 4)))}
    <span>{"$".repeat(Math.max(0, 4 - priceNumber))}</span>
  </Dollars>
);

/** "Free" / "Free trial" (with a check) or a quiet "No free trial". */
export const TrialLabel = ({
  hasFreeTrial,
  isFree,
}: {
  hasFreeTrial: boolean;
  isFree: boolean;
}) => {
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const positive = isFree || hasFreeTrial;
  const title = isFree
    ? t("tags.free")
    : hasFreeTrial
    ? t("tags.free_trial")
    : t("tags.no_free_trial");
  return (
    <Trial $positive={positive}>
      {positive && <Check size={13} strokeWidth={2} aria-hidden={true} />}
      {title}
    </Trial>
  );
};

/** "Top seller", "20% OFF" and "New" as quiet status chips. */
export const GroupTagPills = ({ group }: { group: MarketplaceSubGroup }) => {
  const { t: tCommon } = useTypedTranslation(LanguageNameSpaces.COMMON);
  const { t } = useTypedTranslation(LanguageNameSpaces.MARKETPLACE);
  const tags = SUB_GROUP_CARD_TAGS.filter(
    (tagName) => !!group?.[TAG_NAME_TO_PROPERTY_NAME[tagName]]
  );
  if (!tags.length) return null;
  return (
    <Tags>
      {tags.map((tagName) => (
        <StatusChip key={tagName} $tone={TAG_TONES[tagName]}>
          {tagName === TagName.SALE
            ? t("tags.discount", {
                discount: group[TAG_NAME_TO_PROPERTY_NAME[tagName]] as number,
              })
            : tCommon(tagName)}
        </StatusChip>
      ))}
    </Tags>
  );
};

type ChipTone = "info" | "sale" | "success";

const TAG_TONES: Partial<Record<TagName, ChipTone>> = {
  [TagName.TOP_SELLER]: "info",
  [TagName.SALE]: "sale",
  [TagName.NEW]: "success",
};

/** Tinted chip colours: a light wash of the tone with its darker text. */
const chipTone = (theme: DefaultTheme, tone: ChipTone) => {
  const tokens = getNewLookTokens(theme);
  const dark = theme.isDarkMode;
  const base = {
    info: tokens.accent,
    sale: dark ? tokens.stop : "#C2610C",
    success: tokens.up,
  }[tone];
  const wash = { info: tokens.accent, sale: tokens.stop, success: tokens.up }[
    tone
  ];
  return css`
    background: ${hexToRGB(wash, dark ? 0.18 : 0.12)};
    box-shadow: none;
    color: ${base};
  `;
};

export const StatusChip = styled.span<{ $tone?: ChipTone }>`
  display: inline-flex;
  align-items: center;
  height: 22px;
  padding: 0 9px;
  border-radius: ${NEW_LOOK_RADIUS.chip}px;
  background: ${(p) => getNewLookTokens(p.theme).well};
  box-shadow: inset 0 0 0 1px ${(p) => getNewLookTokens(p.theme).hairline};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  ${newLookType.meta};
  font-weight: 500;
  white-space: nowrap;
  ${(p) => p.$tone && chipTone(p.theme, p.$tone)}
`;

const Logo = styled.img<{ $size: number; $color?: string }>`
  width: ${(p) => p.$size}px;
  height: ${(p) => p.$size}px;
  flex: none;
  display: grid;
  place-items: center;
  object-fit: cover;
  border-radius: ${(p) => (p.$size >= 40 ? 12 : NEW_LOOK_RADIUS.control)}px;
  background: ${(p) => p.$color ?? getNewLookTokens(p.theme).well};
  color: #fff;
  font-size: ${(p) => Math.round(p.$size * 0.42)}px;
  font-weight: 500;
`;
const CompactTitle = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;
const CompactName = styled.span`
  min-width: 0;
  ${newLookPhoneType.headerTitle};
  color: ${(p) => getNewLookTokens(p.theme).ink};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
const Muted = styled.span`
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Rating = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) => getNewLookTokens(p.theme).secondary};
  white-space: nowrap;
  @media (max-width: ${phoneMaxWidth}) {
    font-size: 12.5px;
  }
  strong {
    margin-left: 2px;
    font-weight: 500;
    color: ${(p) => getNewLookTokens(p.theme).ink};
  }
`;
const Stars = styled.span`
  display: inline-flex;
  gap: 1px;
  svg {
    color: ${(p) => p.theme.colors.yellow};
  }
  svg.off {
    color: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  }
`;
const Pnl = styled.span<{ $value: number; $role: FigureRole }>`
  ${(p) => newLookType[p.$role]};
  ${(p) =>
    p.$role === "meta" &&
    css`
      font-weight: 500;
    `}
  color: ${(p) => directionColor(getNewLookTokens(p.theme), p.$value)};
  white-space: nowrap;
`;
const Dollars = styled.span`
  ${newLookType.label};
  color: ${(p) => getNewLookTokens(p.theme).accent};
  font-weight: 500;
  letter-spacing: 1px;
  white-space: nowrap;
  span {
    color: ${(p) => getNewLookTokens(p.theme).hairlineStrong};
  }
`;
const Trial = styled.span<{ $positive: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${newLookType.meta};
  color: ${(p) =>
    p.$positive
      ? getNewLookTokens(p.theme).secondary
      : getNewLookTokens(p.theme).muted};
  white-space: nowrap;
`;
const Tags = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  min-width: 0;
`;
